import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { bugoByMemberIdStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import { useOutletContext } from 'react-router-dom';

// 발인일로부터 이 일수를 "초과"하면 개인정보를 노출하지 않는다.
export const COURTESY_ANONYMIZE_DAYS = 60;

export type CourtesyAccessStatus =
  | 'loading'
  | 'invalid' // 해당 부고 없음(404) / 조회 실패
  | 'anonymous' // 기간 경과·부고 정리됨 → 개인정보 없이 일반 답례글만
  | 'open';

export interface CourtesyAccessResult {
  access: CourtesyAccessStatus;
}

/** 답례인사 하위 라우트가 게이트로부터 전달받는 값 */
export interface CourtesyOutletContext {
  // true면 고인명·상주명·장례정보를 일절 표시하지 않는다.
  anonymous: boolean;
}

export const useCourtesyOutletContext = (): CourtesyOutletContext =>
  useOutletContext<CourtesyOutletContext | null>() ?? { anonymous: false };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// 시각을 무시하고 날짜(자정) 기준으로 비교해 시간대/시분에 의한 오차를 없앤다.
const startOfDay = (date: Date): number => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * 답례인사(courtesy) 페이지의 개인정보 노출 여부를 판정한다.
 * - 부고 없음(서버 404) / 조회 실패 → 'invalid'
 * - 부고 정리됨(meta.isDeleted) "그리고" 발인 후 60일 초과 → 'anonymous'
 * - 그 외 → 'open'
 *
 * 두 조건은 반드시 AND다. isDeleted는 발인 5일 뒤쯤 붙어서 단독으로 쓰면
 * 정작 답례인사를 가장 많이 보내는 시기에 고인·상주 정보가 사라진다.
 *
 * 답례인사는 상주가 직접 공유하는 "생성기"라 수신자 개인정보가 없다.
 * 따라서 기간이 지나도 막지 않고, 고인명·상주명·장례정보만 감춘 채 계속 쓸 수 있게 한다.
 *
 * 부고 조회는 BugoMemberIdRouter의 useBugoByMemberIdHook이 이미 수행하므로
 * 여기서는 recoil atom을 읽기만 한다(중복 fetch 방지).
 * 발인일 데이터가 없으면 막지 않고 'open'으로 둔다(데이터 미비로 정상 유저 차단 방지).
 */
export const useCourtesyAccess = (memberId: string): CourtesyAccessResult => {
  const { status, data } = useRecoilValue(bugoByMemberIdStateSelectorFamily(memberId));

  return useMemo<CourtesyAccessResult>(() => {
    // 서버가 해당 부고를 못 찾으면 404를 던진다 → 로딩을 계속 돌리지 않고 안내로 끝낸다.
    if (status === 'error') {
      return { access: 'invalid' };
    }

    if (status === 'idle' || status === 'loading') {
      return { access: 'loading' };
    }

    if (!data) {
      return { access: 'invalid' };
    }

    const fevent = data.fevent;
    const isDeleted = !!fevent?.meta?.isDeleted;

    // 발인일은 정리된(isDeleted) 부고에도 내려온다.
    const rawCoffinOut = fevent?.deceasedInfo?.coffinOut?.date ?? null;
    const coffinOutDate = rawCoffinOut ? new Date(rawCoffinOut) : null;
    const pastAnonymizeWindow =
      !!coffinOutDate &&
      !Number.isNaN(coffinOutDate.getTime()) &&
      Math.floor((startOfDay(new Date()) - startOfDay(coffinOutDate)) / MS_PER_DAY) >
        COURTESY_ANONYMIZE_DAYS;

    // isDeleted는 발인 5일 뒤쯤 붙으므로 단독 기준이 될 수 없다(그때가 답례인사 성수기).
    // 데이터가 정리됐고(AND) 발인 후 60일도 지난 경우에만 개인정보를 감춘다.
    if (isDeleted && pastAnonymizeWindow) {
      return { access: 'anonymous' };
    }

    return { access: 'open' };
  }, [status, data]);
};
